import React, { useState, useMemo, useEffect } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import Badge from "react-bootstrap/Badge";
import Accordion from "react-bootstrap/Accordion";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import WalkthroughInteractivo from "./WalkthroughInteractivo";
import "./ExamenSimple.scss";

const ExamenSimple = ({ sinsdb, selectedSinIds, onAddSinId, onRemoveSinItem, customSins, onRemoveCustomSin, onAddCustomSin }) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [showCustomSinModal, setShowCustomSinModal] = useState(false);
  const [customSinText, setCustomSinText] = useState("");
  const [activeAccordionKey, setActiveAccordionKey] = useState(() => {
    try {
      const stored = localStorage.getItem("accordionState");
      return stored || "1";
    } catch {
      return "1";
    }
  });

  // Guardar estado del accordion en localStorage
  useEffect(() => {
    try {
      if (activeAccordionKey) {
        localStorage.setItem("accordionState", activeAccordionKey);
      } else {
        localStorage.removeItem("accordionState");
      }
    } catch (error) {
      console.error("Error saving accordion state:", error);
    }
  }, [activeAccordionKey]);

  // Obtener pecados seleccionados
  const selectedSins = useMemo(() => {
    return selectedSinIds.map((id) => ({
      id: id,
      text: t(`sins.${id}.text_past`),
    }));
  }, [selectedSinIds, t]);

  const totalSelected = selectedSins.length + customSins.length;

  // Filtrar mandamientos y pecados según búsqueda
  const filteredCommandments = useMemo(() => {
    if (!searchTerm) return sinsdb.commandments;

    return sinsdb.commandments.filter((cmd) => {
      const cmdTitle = t(`commandments.${cmd.commandment_id}.title`).toLowerCase();
      const cmdText = t(`commandments.${cmd.commandment_id}.text`).toLowerCase();

      if (cmdTitle.includes(searchTerm.toLowerCase()) || cmdText.includes(searchTerm.toLowerCase())) {
        return true;
      }

      const sinsInCmd = sinsdb.sins.filter((s) => s.commandment_id === cmd.commandment_id);
      return sinsInCmd.some((sin) => {
        const sinText = t(`sins.${sin.sin_id}.text`).toLowerCase();
        return sinText.includes(searchTerm.toLowerCase());
      });
    });
  }, [sinsdb, searchTerm, t]);

  // Resetear activeAccordionKey si el mandamiento activo ya no está en los resultados filtrados
  useEffect(() => {
    if (activeAccordionKey && searchTerm) {
      const isActiveInFiltered = filteredCommandments.some(
        (cmd) => String(cmd.commandment_id) === activeAccordionKey
      );
      if (!isActiveInFiltered && filteredCommandments.length > 0) {
        setActiveAccordionKey(String(filteredCommandments[0].commandment_id));
      }
    }
  }, [filteredCommandments, activeAccordionKey, searchTerm]);

  // Handlers
  const handleSinToggle = (sinId) => {
    if (selectedSinIds.includes(sinId)) {
      onRemoveSinItem({ id: sinId });
    } else {
      onAddSinId(sinId);
    }
  };

  const handleAddCustomSin = () => {
    if (customSinText.trim()) {
      onAddCustomSin(customSinText.trim());
      setCustomSinText("");
      setShowCustomSinModal(false);
    }
  };

  const handleClearAll = () => {
    selectedSinIds.forEach((id) => onRemoveSinItem({ id: id }));
    customSins.forEach((text) => onRemoveCustomSin(text));
  };

  const startWalkthrough = () => {
    if (totalSelected > 0) {
      setShowWalkthrough(true);
    }
  };

  // Vista Walkthrough Interactivo
  if (showWalkthrough) {
    return (
      <WalkthroughInteractivo
        selectedSins={selectedSins}
        customSins={customSins}
        onFinish={() => setShowWalkthrough(false)}
        onBack={() => setShowWalkthrough(false)}
      />
    );
  }

  // Vista principal de examen
  return (
    <div className="examen-simple">
      <Container className="py-4">
        <Row className="justify-content-center">
          <Col xs={12} md={10} lg={8} xl={7}>
            {/* Header compacto */}
            <div className="examen-header-simple mb-4">
              <h1 className="text-center mb-2">{t("examen_enhanced.title")}</h1>
              <p className="text-center text-muted mb-4">{t("examen_enhanced.subtitle")}</p>

              {/* Barra de búsqueda */}
              <div className="search-container">
                <i className="fa fa-search search-icon"></i>
                <Form.Control
                  type="text"
                  placeholder={t("examen_enhanced.search_placeholder")}
                  className="search-bar-simple"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <Button
                    variant="link"
                    className="search-clear"
                    onClick={() => setSearchTerm("")}
                  >
                    <i className="fa fa-times"></i>
                  </Button>
                )}
              </div>

              {/* Contador compacto con acciones */}
              <div className="counter-bar">
                <div className="counter-info">
                  <span className="counter-number">{totalSelected}</span>
                  <span className="counter-label">{t("examen_enhanced.sins_selected")}</span>
                </div>
                <div className="counter-actions">
                  {totalSelected > 0 && (
                    <>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="action-btn"
                        onClick={handleClearAll}
                      >
                        <i className="fa fa-trash-o"></i>
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        className="action-btn confess-action-btn"
                        onClick={startWalkthrough}
                      >
                        <i className="fa fa-check"></i> {t("examen_enhanced.ready_to_confess")}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Lista de mandamientos simplificada */}
            <Accordion activeKey={activeAccordionKey} className="commandments-accordion-simple">
              {filteredCommandments.map((cmd, idx) => {
                const cmdTitle = t(`commandments.${cmd.commandment_id}.title`);
                const cmdText = t(`commandments.${cmd.commandment_id}.text`);
                const sinsInCmd = sinsdb.sins.filter((s) => s.commandment_id === cmd.commandment_id);
                const selectedInCmd = sinsInCmd.filter((s) => selectedSinIds.includes(s.sin_id)).length;

                return (
                  <Accordion.Item eventKey={String(cmd.commandment_id)} key={cmd.commandment_id}>
                    <Accordion.Header
                      onClick={(e) => {
                        const cmdIdStr = String(cmd.commandment_id);
                        if (activeAccordionKey === cmdIdStr) {
                          setActiveAccordionKey(null);
                        } else {
                          setActiveAccordionKey(cmdIdStr);
                        }
                      }}
                    >
                      <div className="cmd-header-simple">
                        <span className="cmd-number-simple">{cmd.commandment_id}</span>
                        <span className="cmd-title-simple">{cmdTitle}</span>
                        {selectedInCmd > 0 && (
                          <Badge bg="primary" className="cmd-badge-simple">
                            {selectedInCmd}
                          </Badge>
                        )}
                      </div>
                    </Accordion.Header>
                    <Accordion.Body>
                      <p className="cmd-text-simple text-muted">{cmdText}</p>
                      <div className="sins-list-simple">
                        {sinsInCmd.map((sin) => {
                          const isSelected = selectedSinIds.includes(sin.sin_id);
                          const sinText = t(`sins.${sin.sin_id}.text`);

                          return (
                            <div
                              key={sin.sin_id}
                              className={`sin-item-simple ${isSelected ? "selected" : ""}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSinToggle(sin.sin_id);
                              }}
                            >
                              <div className="sin-checkbox-simple">
                                {isSelected && <i className="fa fa-check"></i>}
                              </div>
                              <span className="sin-text-simple">{sinText}</span>
                            </div>
                          );
                        })}
                        {/* Opción para agregar pecado personalizado */}
                        <div
                          className="sin-item-simple custom-sin-option"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowCustomSinModal(true);
                          }}
                        >
                          <div className="sin-checkbox-simple">
                            <i className="fa fa-plus"></i>
                          </div>
                          <span className="sin-text-simple text-muted">
                            {t("examen_simple.add_custom_sin")}
                          </span>
                        </div>
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                );
              })}
            </Accordion>

            {/* Estado vacío */}
            {totalSelected === 0 && (
              <div className="empty-state text-center py-5">
                <i className="fa fa-hand-o-up empty-icon"></i>
                <p className="text-muted mt-3">{t("examen_simple.empty_state")}</p>
              </div>
            )}
          </Col>
        </Row>
      </Container>

      {/* Modal para agregar pecado personalizado */}
      <Modal show={showCustomSinModal} onHide={() => setShowCustomSinModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("addbutton.add-custom-sin")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Control
            as="textarea"
            rows={3}
            placeholder={t("addbutton.i-sinned-by")}
            value={customSinText}
            onChange={(e) => setCustomSinText(e.target.value)}
            autoFocus
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCustomSinModal(false)}>
            {t("addbutton.cancel")}
          </Button>
          <Button variant="primary" onClick={handleAddCustomSin}>
            <i className="fa fa-plus"></i> {t("addbutton.add")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ExamenSimple;
